// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { 
  Activity, CheckCircle, XCircle, AlertCircle, 
  Play, RefreshCw, Database, Cpu, Book,
  Upload, Settings, Monitor, Server, 
  Wifi, HardDrive, Clock, FileText,
  Eye, EyeOff, Download
} from 'lucide-react';
import { apiJSON, apiFetch } from '../config/api';
import ApiService from '../services/api';
import notificationService, { handleApiError } from '../utils/notificationService';
import './SystemHealth.css';

const SystemHealth = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState({});
  const [services, setServices] = useState({});
  const [systemOverview, setSystemOverview] = useState(null); // FIX-007 (refs AUD-012)
  const [tasks, setTasks] = useState([]); // FIX-011 (refs AUD-019)
  const [cacheClearing, setCacheClearing] = useState(false);
  const [logs, setLogs] = useState({ frontend: [], backend: [] });
  const [showLogs, setShowLogs] = useState(false);
  const [logService, setLogService] = useState('backend');

  useEffect(() => {
    loadSystemHealth();
    const interval = setInterval(loadSystemHealth, 30000); // Actualiser toutes les 30s
    return () => clearInterval(interval);
  }, []);

  const loadSystemHealth = async () => {
    try {
      setLoading(true);
      
      // Charger les statuts des services
      try {
        const healthData = await apiJSON('admin/health');
        if (healthData.success) {
          setServices(healthData.services || {});
        }
      } catch (error) {
        console.warn('Statuts services non disponibles:', error.message);
        setServices({});
      }
      try {
        const overview = await ApiService.getSystemOverview();
        setSystemOverview(overview);
      } catch (_) {
        setSystemOverview(null);
      }
      try {
        const tasksData = await ApiService.listTasks();
        setTasks(Array.isArray(tasksData?.tasks) ? tasksData.tasks : (tasksData || []));
      } catch (_) {
        setTasks([]);
      }

      // Charger les logs si activés
      if (showLogs) {
        await loadLogs();
      }

    } catch (error) {
      handleApiError(error, 'Chargement santé système');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const data = await ApiService.getAdminLogs({ service: logService, limit: 50 });
      if (data && data.success) {
        setLogs(prev => ({
          ...prev,
          [logService]: data.logs || []
        }));
      }
    } catch (error) {
      console.warn('Logs non disponibles:', error.message || error);
    }
  };

  const testDefinitions = [
    {
      id: 'api-connection',
      name: 'Connexion API',
      description: 'Vérifier la connexion au backend',
      category: 'API',
      test: async () => {
        const data = await apiJSON('health');
        return data.status === 'ok' ? 'success' : 'error';
      }
    },
    {
      id: 'books-endpoint',
      name: 'Endpoint Livres',
      description: 'Tester la récupération des livres',
      category: 'API',
      test: async () => {
        const data = await apiJSON('books?limit=5');
        return data.books ? 'success' : 'error';
      }
    },
    {
      id: 'conversations-endpoint',
      name: 'Endpoint Conversations',
      description: 'Tester la gestion des conversations',
      category: 'API',
      test: async () => {
        const data = await apiJSON('conversations/list');
        return data.conversations !== undefined ? 'success' : 'error';
      }
    },
    {
      id: 'ai-providers',
      name: 'Providers IA',
      description: 'Vérifier les providers IA disponibles',
      category: 'IA',
      test: async () => {
        const data = await apiJSON('conversations/ai-providers');
        return data.providers ? 'success' : 'error';
      }
    },
    {
      id: 'upload-endpoint',
      name: 'Endpoint Upload',
      description: 'Tester l\'endpoint d\'upload',
      category: 'Upload',
      test: async () => {
        try {
          const response = await apiFetch('import', {
            method: 'POST',
            body: new FormData()
          });
          return response.status === 400 ? 'success' : 'warning';
        } catch {
          return 'error';
        }
      }
    },
    {
      id: 'sse-connection',
      name: 'Connexion SSE',
      description: 'Tester les événements temps réel',
      category: 'SSE',
      test: async () => {
        try {
          const response = await apiFetch('sse/events/test');
          return response.ok ? 'success' : 'error';
        } catch {
          return 'error';
        }
      }
    },
    {
      id: 'backup-endpoint',
      name: 'Endpoint Backup',
      description: 'Tester les fonctionnalités de backup',
      category: 'Admin',
      test: async () => {
        try {
          const response = await apiFetch('backup/status');
          return response.ok ? 'success' : 'warning';
        } catch {
          return 'error';
        }
      }
    },
    {
      id: 'cache-endpoint',
      name: 'Endpoint Cache',
      description: 'Tester la gestion du cache',
      category: 'Admin',
      test: async () => {
        try {
          const response = await apiFetch('cache/status');
          return response.ok ? 'success' : 'warning';
        } catch {
          return 'error';
        }
      }
    }
  ];

  const runTest = async (testDef) => {
    try {
      setResults(prev => ({ ...prev, [testDef.id]: 'running' }));
      
      const result = await testDef.test();
      
      setResults(prev => ({ ...prev, [testDef.id]: result }));
      
      if (result === 'success') {
        notificationService.showToast(`✅ ${testDef.name}: OK`, 'success', 2000);
      } else if (result === 'warning') {
        notificationService.showToast(`⚠️ ${testDef.name}: Partiel`, 'warning', 2000);
      } else {
        notificationService.showToast(`❌ ${testDef.name}: Échec`, 'error', 3000);
      }
      
    } catch (error) {
      setResults(prev => ({ ...prev, [testDef.id]: 'error' }));
      handleApiError(error, `Test ${testDef.name}`);
    }
  };

  const runAllTests = async () => {
    setRunning(true);
    
    for (const testDef of testDefinitions) {
      await runTest(testDef);
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    setRunning(false);
  };

  const getResultIcon = (result) => {
    switch (result) {
      case 'success':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'error':
        return <XCircle size={16} className="text-red-500" />;
      case 'warning':
        return <AlertCircle size={16} className="text-yellow-500" />;
      case 'running':
        return <RefreshCw size={16} className="text-blue-500 animate-spin" />;
      default:
        return <Activity size={16} className="text-gray-400" />;
    }
  };

  const getResultText = (result) => {
    switch (result) {
      case 'success':
        return 'Succès';
      case 'error':
        return 'Échec';
      case 'warning':
        return 'Partiel';
      case 'running':
        return 'En cours...';
      default:
        return 'Non testé';
    }
  };

  const getServiceStatus = (service) => {
    if (!services[service]) return 'unknown';
    return services[service].status || 'unknown';
  };

  const getServiceIcon = (service) => {
    switch (service) {
      case 'backend':
        return <Server size={20} />;
      case 'database':
        return <Database size={20} />;
      case 'ollama':
        return <Cpu size={20} />;
      case 'vector_store':
        return <Book size={20} />;
      case 'cache':
        return <Activity size={20} />;
      default:
        return <Monitor size={20} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
      case 'success':
        return 'green';
      case 'warning':
        return 'yellow';
      case 'error':
      case 'unhealthy':
        return 'red';
      default:
        return 'gray';
    }
  };

  const formatLogEntry = (entry) => {
    const timestamp = new Date(entry.timestamp).toLocaleString('fr-FR');
    return {
      ...entry,
      formattedTime: timestamp
    };
  };

  const downloadLogs = async () => {
    try {
      const response = await apiFetch(`admin/logs/download?service=${encodeURIComponent(logService)}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${logService}_logs_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      handleApiError(error, 'Téléchargement logs');
    }
  };

  if (loading) {
    return (
      <div className="system-health app-page">
        <div className="loading">
          <RefreshCw className="spinner" />
          <p>Chargement de la santé système...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="system-health app-page">
      <div className="health-header">
        <h1><Activity /> Santé & Tests</h1>
        <p>Diagnostics système et visualisation des logs</p>
      </div>

      {/* Onglets de navigation */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <Monitor /> Vue d'ensemble
        </button>
        <button 
          className={`tab ${activeTab === 'tests' ? 'active' : ''}`}
          onClick={() => setActiveTab('tests')}
        >
          <Play /> Tests
        </button>
        <button 
          className={`tab ${activeTab === 'logs' ? 'active' : ''}`}
          onClick={() => setActiveTab('logs')}
        >
          <FileText /> Logs
        </button>
      </div>

      {/* Contenu des onglets */}
      <div className="tab-content">
        {/* Vue d'ensemble */}
        {activeTab === 'overview' && (
          <div className="overview-section">
            <div className="section-header">
              <h2><Monitor /> Statut des Services</h2>
              <button 
                onClick={loadSystemHealth}
                className="btn-secondary"
              >
                <RefreshCw /> Actualiser
              </button>
            </div>

            <div className="services-grid">
              {Object.entries(services).map(([service, data]) => (
                <div key={service} className={`service-card ${getServiceStatus(service)}`}>
                  <div className="service-icon">
                    {getServiceIcon(service)}
                  </div>
                  <div className="service-info">
                    <h3>{service.replace('_', ' ').toUpperCase()}</h3>
                    <p className={`status ${getStatusColor(getServiceStatus(service))}`}>
                      {getServiceStatus(service) === 'healthy' ? 'Opérationnel' : 
                       getServiceStatus(service) === 'warning' ? 'Attention' :
                       getServiceStatus(service) === 'error' ? 'Erreur' : 'Inconnu'}
                    </p>
                    {data.response_time && (
                      <p className="response-time">
                        <Clock size={16} />
                        {data.response_time}ms
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {systemOverview && (
              <div className="system-overview-cards" style={{ marginTop: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div className="stat-card">
                  <div className="stat-icon"><Book size={20} /></div>
                  <div className="stat-content">
                    <h4>Livres</h4>
                    <p>{systemOverview.books?.total ?? 0} total, {systemOverview.books?.recent ?? 0} récents</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon"><Database size={20} /></div>
                  <div className="stat-content">
                    <h4>Vector store</h4>
                    <p>Statut: {systemOverview.vector_store?.status ?? '—'}, embeddings: {systemOverview.vector_store?.embeddings ?? '—'}</p>
                  </div>
                </div>
              </div>
            )}

            {/* FIX-011 (refs AUD-019): Cache et tâches */}
            <div style={{ marginTop: 16 }}>
              <h3 style={{ marginBottom: 8 }}>Cache et tâches</h3>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <select id="cache-type" defaultValue="">
                  <option value="">Tous les caches</option>
                  <option value="rag">RAG</option>
                  <option value="embedding">Embedding</option>
                  <option value="vector">Vector</option>
                </select>
                <button
                  className="btn-secondary"
                  disabled={cacheClearing}
                  onClick={async () => {
                    setCacheClearing(true);
                    try {
                      const type = document.getElementById('cache-type')?.value || '';
                      await ApiService.clearCache(type || undefined);
                      notificationService.showToast('Cache vidé', 'success');
                      loadSystemHealth();
                    } catch (e) {
                      notificationService.showToast(e?.message || 'Erreur', 'error');
                    } finally {
                      setCacheClearing(false);
                    }
                  }}
                >
                  {cacheClearing ? '...' : 'Vider le cache'}
                </button>
              </div>
              {tasks.length > 0 && (
                <ul style={{ marginTop: 8, fontSize: 14 }}>
                  {tasks.slice(0, 10).map((t, i) => (
                    <li key={i}>{typeof t === 'object' ? (t.name || t.id || JSON.stringify(t)) : t}</li>
                  ))}
                </ul>
              )}
            </div>

            <div className="system-stats">
              <h3>Statistiques Système</h3>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon">
                    <Database />
                  </div>
                  <div className="stat-content">
                    <h4>Services</h4>
                    <p>{Object.keys(services).length} services surveillés</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <CheckCircle />
                  </div>
                    <div className="stat-content">
                    <h4>Opérationnels</h4>
                    <p>{Object.values(services).filter(s => s.status === 'healthy').length} services</p>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon">
                    <AlertCircle />
                  </div>
                  <div className="stat-content">
                    <h4>Problèmes</h4>
                    <p>{Object.values(services).filter(s => s.status !== 'healthy').length} services</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tests */}
        {activeTab === 'tests' && (
          <div className="tests-section">
            <div className="section-header">
              <h2><Play /> Tests de Connectivité</h2>
              <div className="test-controls">
                <button
                  className="btn-primary"
                  onClick={runAllTests}
                  disabled={running}
                >
                  <Play size={16} />
                  {running ? 'Tests en cours...' : 'Lancer tous les tests'}
                </button>
                
                <button
                  className="btn-secondary"
                  onClick={() => setResults({})}
                  disabled={running}
                >
                  <RefreshCw size={16} />
                  Réinitialiser
                </button>
              </div>
            </div>

            <div className="test-summary">
              <div className="summary-stats">
                <div className="stat">
                  <span className="stat-label">Total:</span>
                  <span className="stat-value">{testDefinitions.length}</span>
                </div>
                <div className="stat success">
                  <span className="stat-label">Succès:</span>
                  <span className="stat-value">
                    {Object.values(results).filter(r => r === 'success').length}
                  </span>
                </div>
                <div className="stat warning">
                  <span className="stat-label">Partiels:</span>
                  <span className="stat-value">
                    {Object.values(results).filter(r => r === 'warning').length}
                  </span>
                </div>
                <div className="stat error">
                  <span className="stat-label">Échecs:</span>
                  <span className="stat-value">
                    {Object.values(results).filter(r => r === 'error').length}
                  </span>
                </div>
              </div>
            </div>

            <div className="test-categories">
              {['API', 'IA', 'Upload', 'SSE', 'Admin'].map(category => (
                <div key={category} className="test-category">
                  <div className="category-header">
                    <h3>{category}</h3>
                  </div>
                  
                  <div className="category-tests">
                    {testDefinitions
                      .filter(test => test.category === category)
                      .map(test => (
                        <div key={test.id} className="test-item">
                          <div className="test-info">
                            <div className="test-name">
                              {test.name}
                            </div>
                            <div className="test-description">
                              {test.description}
                            </div>
                          </div>
                          
                          <div className="test-actions">
                            <div className="test-result">
                              {getResultIcon(results[test.id])}
                              <span className="result-text">
                                {getResultText(results[test.id])}
                              </span>
                            </div>
                            
                            <button
                              className="btn-secondary"
                              onClick={() => runTest(test)}
                              disabled={running}
                            >
                              <Play size={16} />
                              Tester
                            </button>
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Logs */}
        {activeTab === 'logs' && (
          <div className="logs-section">
            <div className="section-header">
              <h2><FileText /> Visualisation des Logs</h2>
              <div className="logs-controls">
                <select 
                  value={logService} 
                  onChange={(e) => setLogService(e.target.value)}
                  className="log-service-select"
                >
                  <option value="backend">Backend</option>
                  <option value="frontend">Frontend</option>
                </select>
                
                <button 
                  onClick={() => setShowLogs(!showLogs)}
                  className="btn-secondary"
                >
                  {showLogs ? <EyeOff /> : <Eye />}
                  {showLogs ? 'Masquer' : 'Afficher'} Logs
                </button>
                
                {showLogs && (
                  <button 
                    onClick={downloadLogs}
                    className="btn-secondary"
                  >
                    <Download />
                    Télécharger
                  </button>
                )}
              </div>
            </div>

            {showLogs && (
              <div className="logs-viewer">
                <div className="logs-header">
                  <h3>Logs {logService === 'backend' ? 'Backend' : 'Frontend'}</h3>
                  <button 
                    onClick={loadLogs}
                    className="btn-secondary"
                  >
                    <RefreshCw />
                    Actualiser
                  </button>
                </div>
                
                <div className="logs-content">
                  {logs[logService].length === 0 ? (
                    <p className="no-logs">Aucun log disponible</p>
                  ) : (
                    <div className="logs-list">
                      {logs[logService].map((entry, index) => {
                        const formattedEntry = formatLogEntry(entry);
                        return (
                          <div key={index} className={`log-entry ${entry.level || 'info'}`}>
                            <div className="log-timestamp">
                              {formattedEntry.formattedTime}
                            </div>
                            <div className="log-level">
                              {entry.level?.toUpperCase() || 'INFO'}
                            </div>
                            <div className="log-message">
                              {entry.message}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemHealth;
